import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate, STATUS_BADGE } from "@/lib/format";
import { printInvoice, printCustomerCombinedInvoice } from "@/lib/invoice";
import { parseKartuUcapan } from "@/lib/ucapanParser";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { useLimits, ShowMore } from "@/components/ShowMore";
import { SupplierDebtRecap } from "@/components/SupplierDebtRecap";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Wallet, Printer, Pencil, Eye, HandCoins, CheckCircle2, ShoppingBag, Receipt, UserCog,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);

const STATUS_FILTERS = [
  { id: "all", label: "Semua Status" },
  { id: "unpaid", label: "Belum Dibayar" },
  { id: "partial", label: "Dibayar Sebagian" },
  { id: "paid", label: "Lunas" },
  { id: "overdue", label: "Jatuh Tempo" },
];

const PERIOD_LABELS = { today: "Hari ini", week: "Minggu ini", month: "Bulan ini", year: "Tahun ini", all: "Semua periode" };

function StatusBadge({ b }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Badge variant="outline" className={STATUS_BADGE[b.payment_status]}>{b.payment_status}</Badge>
      {b.overdue && <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300 text-[10px] px-1.5 py-0">Jatuh Tempo</Badge>}
    </div>
  );
}

export default function CustomerBills() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("customer-bills");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selLunas, setSelLunas] = useState([]);
  const [adminFilter, setAdminFilter] = useState("all");
  const [adminList, setAdminList] = useState([]);
  const { lim, more } = useLimits(30);
  const [bills, setBills] = useState([]);
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(true);

  const qstr = () => (period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`);

  const load = () => {
    setLoading(true);
    const p = `${qstr()}${search ? `&search=${encodeURIComponent(search)}` : ""}`;
    api.get(`/customer-bills?${p}`).then((r) => setBills(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, range.start, range.end]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => {
    api.get("/meta/options").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/customer-bills/admins").then((r) => setAdminList(r.data.admins || [])).catch(() => {});
  }, []);

  const periodLabel = () => (period === "custom" ? `${range.start} s/d ${range.end}` : PERIOD_LABELS[period]);

  const admins = useMemo(() => {
    const set = new Set(adminList);
    bills.forEach((b) => set.add(b.created_by_name || "Tanpa Admin"));
    return Array.from(set).sort((a, b) => (a === "Tanpa Admin") - (b === "Tanpa Admin") || a.localeCompare(b));
  }, [bills, adminList]);
  const matchCbStatus = (b, f) => {    if (f === "all") return true;
    if (f === "unpaid") return b.payment_status === "Belum Dibayar";
    if (f === "partial") return b.payment_status === "Sebagian";
    if (f === "paid") return b.payment_status === "Lunas";
    if (f === "overdue") return b.overdue;
    return true;
  };

  const selectTab = (id) => { setStatus(id); setSelLunas([]); };

  const filtered = useMemo(() => bills.filter((b) => {
    if (adminFilter !== "all" && (b.created_by_name || "Tanpa Admin") !== adminFilter) return false;
    if (!matchCbStatus(b, status)) return false;
    return true;
  }), [bills, adminFilter, status]);

  const lunasBills = filtered.filter((b) => b.payment_status === "Lunas");
  const selRows = lunasBills.filter((b) => selLunas.includes(b._id));
  const toggleLunas = (b) => setSelLunas((s) => (s.includes(b._id) ? s.filter((x) => x !== b._id) : [...s, b._id]));
  const printSelLunas = () => {
    if (!selRows.length) return;
    const adminNames = [...new Set(selRows.map((r) => r.created_by_name || "Tanpa Admin"))];
    if (adminNames.length > 1) { toast.error("Order milik admin berbeda tidak dapat digabung dalam satu invoice"); return; }
    const custNames = [...new Set(selRows.map((r) => r.customer_name).filter(Boolean))];
    printCustomerCombinedInvoice({
      admin: adminNames[0] || "-",
      customer: custNames.length === 1 ? custNames[0] : `${custNames[0] || "Pelanggan"} dll`,
      phone: custNames.length === 1 ? (selRows[0].customer_phone || "") : "",
      periodLabel: periodLabel(),
      bills: selRows,
    });
  };

  const rekap = useMemo(() => ({
    count: filtered.length,
    tagihan: sum(filtered, (b) => b.amount),
    dibayar: sum(filtered, (b) => b.paid),
    piutang: sum(filtered.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining),
  }), [filtered]);

  const groups = useMemo(() => {
    const byAdmin = {};
    filtered.forEach((b) => {
      const admin = b.created_by_name || "Tanpa Admin";
      byAdmin[admin] = byAdmin[admin] || { admin, customers: {}, bills: [] };
      byAdmin[admin].bills.push(b);
      const cust = b.customer_name || "Tanpa Pelanggan";
      byAdmin[admin].customers[cust] = byAdmin[admin].customers[cust] || { customer: cust, phone: b.customer_phone || "", bills: [] };
      byAdmin[admin].customers[cust].bills.push(b);
    });
    return Object.values(byAdmin).map((g) => ({ ...g, customers: Object.values(g.customers) }));
  }, [filtered]);

  const [detailFor, setDetailFor] = useState(null);

  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account: "Bank BCA", date: today() });
  const [payProof, setPayProof] = useState("");
  const [payProofBusy, setPayProofBusy] = useState(false);

  const uploadProof = async (file) => {
    if (!file) return;
    setPayProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setPayProof(r.data.path);
      toast.success("Bukti pembayaran terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah bukti"); }
    finally { setPayProofBusy(false); }
  };

  const viewProof = async (path) => {
    try {
      const r = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Gagal membuka bukti pembayaran"); }
  };

  const submitPay = async () => {
    if (!payForm.amount) { toast.error("Isi jumlah pembayaran"); return; }
    if (!payProof) { toast.error("Bukti gambar pembayaran wajib disertakan"); return; }
    try {
      await api.patch(`/customer-bills/${payFor._id}/pay`, { ...payForm, amount: parseFloat(payForm.amount), proof_image: payProof });
      toast.success("Pembayaran dicatat");
      setPayFor(null); setPayForm({ amount: "", account: "Bank BCA", date: today() }); setPayProof("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const openEdit = (b) => {
    setEditFor(b);
    setEform({
      customer_name: b.customer_name || "", customer_phone: b.customer_phone || "",
      category: b.category || "", quantity: String(b.quantity || 1),
      unit_sell: String(b.unit_sell != null ? b.unit_sell : b.amount || 0),
      unit_base: String(b.unit_base != null ? b.unit_base : b.base_price || 0),
      cashback: String(b.cashback || 0), customer_due_date: b.due_date || b.date || today(),
      description: b.description || "", ucapan: b.ucapan || "", dari: b.dari || "", alamat: b.alamat || "",
    });
  };
  const submitEdit = async () => {
    const qty = parseInt(eform.quantity) || 1;
    try {
      await api.patch(`/customer-bills/${editFor._id}`, {
        customer_name: eform.customer_name || null,
        customer_phone: eform.customer_phone || null,
        category: eform.category || null,
        quantity: qty,
        unit_sell: parseFloat(eform.unit_sell) || 0,
        unit_base: parseFloat(eform.unit_base) || 0,
        cashback: parseFloat(eform.cashback) || 0,
        customer_due_date: eform.customer_due_date || null,
        description: eform.description || null,
        ucapan: eform.ucapan || null,
        dari: eform.dari || null,
        alamat: eform.alamat || null,
      });
      toast.success("Tagihan diperbarui");
      setEditFor(null); setEform(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cards = [
    { label: "Total Order", value: null, text: String(rekap.count), sub: "tagihan pelanggan", icon: ShoppingBag, tone: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"] },
    { label: "Total Tagihan", value: rekap.tagihan, sub: "nilai order", icon: Receipt, tone: ["bg-indigo-100 dark:bg-indigo-950", "text-indigo-600"] },
    { label: "Sudah Dibayar", value: rekap.dibayar, sub: "diterima", icon: CheckCircle2, tone: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"] },
    { label: "Total Piutang", value: rekap.piutang, sub: "belum lunas", icon: HandCoins, tone: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
  ];

  const ActionButtons = ({ b, size = "icon" }) => (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => setDetailFor(b)} data-testid={`bill-detail-${b._id}`} title="Detail"><Eye className="h-4 w-4" /></Button>
      {b.payment_status !== "Lunas" && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => printInvoice(b)} data-testid={`bill-invoice-${b._id}`} title="Cetak Invoice"><Printer className="h-4 w-4" /></Button>
      )}
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => openEdit(b)} data-testid={`bill-edit-${b._id}`} title="Edit"><Pencil className="h-4 w-4" /></Button>
      {b.remaining > 0 && (
        <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => { setPayProof(""); setPayFor(b); setPayForm({ amount: String(b.remaining), account: "Bank BCA", date: today() }); }} data-testid={`bill-pay-${b._id}`}>
          <Wallet className="h-3.5 w-3.5" /> Bayar
        </Button>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><Receipt className="h-5 w-5 text-[#C9A227]" /> Tagihan Pelanggan</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Tagihan dari order masuk, dikelompokkan per admin. Status terhitung otomatis dari pembayaran tercatat.</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-summary">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`billing-card-${c.label}`} className="p-5">
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-11 w-11 rounded-xl ${c.tone[0]}`}><c.icon className={`h-5 w-5 ${c.tone[1]}`} /></div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</p>
                <p className="font-heading text-lg font-extrabold truncate">{c.text != null ? c.text : formatIDR(c.value)}</p>
                <p className="text-[10px] text-muted-foreground">{c.sub}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-2.5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="billing-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama pelanggan, no. telepon, atau no. order..." className="pl-9" />
        </div>
        <Select value={adminFilter} onValueChange={setAdminFilter}>
          <SelectTrigger data-testid="billing-admin-filter" className="lg:w-48"><SelectValue placeholder="Admin" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Admin</SelectItem>{admins.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="billing-status-tabs">
        {STATUS_FILTERS.map((s) => {
          const base = adminFilter === "all" ? bills : bills.filter((b) => (b.created_by_name || "Tanpa Admin") === adminFilter);
          const rows = base.filter((b) => matchCbStatus(b, s.id));
          const amt = rows.reduce((a, x) => a + (x.amount || 0), 0);
          const sisa = rows.reduce((a, x) => a + (x.remaining || 0), 0);
          return (
            <button key={s.id} type="button" data-testid={`cb-tab-${s.id}`} onClick={() => selectTab(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${status === s.id ? "bg-[#0A1633] text-white border-[#0A1633]" : "bg-card text-muted-foreground border-border hover:border-[#C9A227]/60"}`}>
              {s.label} · {rows.length} · {formatIDR(amt)}{sisa > 0 && s.id !== "paid" && s.id !== "all" ? ` (sisa ${formatIDR(sisa)})` : ""}
            </button>
          );
        })}
      </div>

      {status === "paid" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="cb-lunas-print-bar">
          <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer" data-testid="cb-select-all-lunas">
            <input type="checkbox" className="h-4 w-4 accent-[#0A1633]"
              checked={lunasBills.length > 0 && selLunas.length === lunasBills.length}
              onChange={() => setSelLunas(selLunas.length === lunasBills.length ? [] : lunasBills.map((b) => b._id))} />
            Pilih Semua
          </label>
          <Button size="sm" variant="gold" className="h-8 text-xs gap-1" disabled={!selRows.length} onClick={printSelLunas} data-testid="cb-print-lunas">
            <Printer className="h-3.5 w-3.5" /> Cetak Invoice Gabungan ({selRows.length} order · {formatIDR(selRows.reduce((a, b) => a + (b.amount || 0), 0))})
          </Button>
          {selLunas.length > 0 && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelLunas([])} data-testid="cb-clear-lunas">Bersihkan Pilihan</Button>}
          <span className="text-[11px] text-muted-foreground">Pilih order Lunas untuk dicetak menjadi satu invoice rekap.</span>
        </div>
      )}

      <SupplierDebtRecap bills={filtered} extraAdmins={adminFilter === "all" ? adminList : [adminFilter]} periodLabel={periodLabel()}
        activeAdmin={adminFilter} title="Rekap Order & Piutang Pelanggan per Admin" partyLabel="Pelanggan" debtLabel="Sisa Piutang"
        partyKey="customer_name" testPrefix="cust-recap"
        onSelectAdmin={(a) => {
          setAdminFilter(adminFilter === a ? "all" : a);
          setTimeout(() => document.getElementById("cust-groups")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        }} />
      <div id="cust-groups" className="scroll-mt-20" />

      {!loading && groups.length === 0 && <Card className="p-10 text-center text-muted-foreground text-sm">Tidak ada tagihan pada filter ini.</Card>}

      {groups.map((g) => (
        <Card key={g.admin} data-testid={`billing-admin-group-${g.admin}`} className="overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#0A1633] text-white">
            <div className="flex items-center gap-2 min-w-0">
              <div className="grid place-items-center h-8 w-8 rounded-lg bg-white/10 border border-[#C9A227]/40 shrink-0"><UserCog className="h-4 w-4 text-[#C9A227]" /></div>
              <div className="min-w-0"><p className="font-heading font-semibold text-sm truncate">{g.admin}</p><p className="text-[11px] text-blue-100/70">{g.bills.length} order</p></div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-blue-100/70">Sisa Piutang</p>
                <p className="font-heading font-bold text-sm">{formatIDR(sum(g.bills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</p>
              </div>
            </div>
          </div>

          {g.customers.slice(0, lim(`c|${g.admin}`)).map((cg) => (
            <div key={cg.customer} className="border-b border-border last:border-0">
              <div className="flex items-center justify-between gap-2 px-4 py-2 bg-secondary/60">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold truncate">{cg.customer}</span>
                  {cg.phone && <span className="text-[10px] text-muted-foreground">{cg.phone}</span>}
                  <span className="text-[10px] text-muted-foreground">({cg.bills.length} order)</span>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap hidden md:inline">Tagihan <b className="text-foreground">{formatIDR(sum(cg.bills, (b) => b.amount))}</b></span>
                  <span className="text-[11px] text-rose-600 whitespace-nowrap hidden md:inline">Sisa {formatIDR(sum(cg.bills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</span>
                  {cg.bills.some((b) => b.payment_status !== "Lunas") && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => printCustomerCombinedInvoice({ admin: g.admin, customer: cg.customer, phone: cg.phone, periodLabel: periodLabel(), bills: cg.bills.filter((b) => b.payment_status !== "Lunas") })} data-testid={`print-cust-${g.admin}-${cg.customer}`}>
                      <Printer className="h-3 w-3" /> Cetak Invoice Pelanggan
                    </Button>
                  )}
                </div>
              </div>

              <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  {status === "paid" && <TableHead className="w-8"></TableHead>}
                  <TableHead className="whitespace-nowrap">Tgl Order</TableHead>
                  <TableHead className="whitespace-nowrap">No. Order</TableHead>
                  <TableHead>Pelanggan</TableHead>
                  <TableHead>Produk</TableHead>
                  <TableHead className="whitespace-nowrap">Jatuh Tempo</TableHead>
                  <TableHead className="text-right whitespace-nowrap">Total Tagihan</TableHead>
                  <TableHead className="text-right">Dibayar</TableHead>
                  <TableHead className="text-right">Sisa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cg.bills.slice(0, lim(`b|${g.admin}|${cg.customer}`)).map((b) => (
                  <TableRow key={b._id} data-testid={`bill-row-${b._id}`}>
                    {status === "paid" && (
                      <TableCell className="w-8">
                        <input type="checkbox" data-testid={`cb-select-${b._id}`} checked={selLunas.includes(b._id)} onChange={() => toggleLunas(b)} className="h-4 w-4 accent-[#0A1633] cursor-pointer" />
                      </TableCell>
                    )}
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(b.date)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm font-medium">{b.order_no}</TableCell>
                    <TableCell>
                      <p className="text-sm font-medium">{b.customer_name || "-"}</p>
                      {b.customer_phone && <p className="text-xs text-muted-foreground">{b.customer_phone}</p>}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`bill-produk-${b._id}`}>
                      <p className="whitespace-nowrap">{b.category} <span className="text-muted-foreground">×{b.quantity || 1}</span></p>
                      {(b.ucapan || b.dari || b.alamat) && (
                        <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground whitespace-pre-line break-words max-w-[260px]">
                          {b.ucapan && <p>Ucapan: {b.ucapan}</p>}
                          {b.dari && <p>Dari: {b.dari}</p>}
                          {b.alamat && <p>Alamat: {b.alamat}</p>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={`whitespace-nowrap text-sm ${b.overdue ? "text-rose-600 font-medium" : ""}`}>{formatDate(b.due_date)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap font-semibold">{formatIDR(b.amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap text-emerald-600">{formatIDR(b.paid)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap text-rose-600">{formatIDR(b.remaining)}</TableCell>
                    <TableCell><StatusBadge b={b} /></TableCell>
                    <TableCell><div className="flex items-center justify-end gap-1"><ActionButtons b={b} /></div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="lg:hidden divide-y divide-border">
            {cg.bills.slice(0, lim(`b|${g.admin}|${cg.customer}`)).map((b) => (
              <div key={b._id} data-testid={`bill-row-${b._id}`} className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{b.order_no} · {b.category}</p>
                    <p className="text-[11px] text-muted-foreground">{formatDate(b.date)} · {b.customer_name || "-"}</p>
                  </div>
                  <StatusBadge b={b} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                  <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-semibold">{formatIDR(b.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Dibayar</span><span className="text-emerald-600">{formatIDR(b.paid)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sisa</span><span className="text-rose-600 font-medium">{formatIDR(b.remaining)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Tempo</span><span className={b.overdue ? "text-rose-600 font-medium" : ""}>{formatDate(b.due_date)}</span></div>
                </div>
                {(b.ucapan || b.dari || b.alamat) && (
                  <div className="space-y-0.5 text-[11px] text-muted-foreground whitespace-pre-line break-words" data-testid={`bill-ucapan-${b._id}`}>
                    {b.ucapan && <p>Ucapan: {b.ucapan}</p>}
                    {b.dari && <p>Dari: {b.dari}</p>}
                    {b.alamat && <p>Alamat: {b.alamat}</p>}
                  </div>
                )}
                <div className="flex justify-end gap-1 pt-0.5">
                  {status === "paid" && (
                    <label className="flex items-center gap-1 mr-auto text-[10px] text-muted-foreground cursor-pointer" data-testid={`cb-select-mobile-${b._id}`}>
                      <input type="checkbox" checked={selLunas.includes(b._id)} onChange={() => toggleLunas(b)} className="h-4 w-4 accent-[#0A1633]" />
                      Pilih utk invoice
                    </label>
                  )}
                  <ActionButtons b={b} />
                </div>
              </div>
            ))}
          </div>
          <ShowMore shown={Math.min(lim(`b|${g.admin}|${cg.customer}`), cg.bills.length)} total={cg.bills.length} label="order"
            onMore={() => more(`b|${g.admin}|${cg.customer}`)} testid={`more-orders-${g.admin}-${cg.customer}`} />
            </div>
          ))}
          <ShowMore shown={Math.min(lim(`c|${g.admin}`), g.customers.length)} total={g.customers.length} label="pelanggan"
            onMore={() => more(`c|${g.admin}`)} testid={`more-customers-${g.admin}`} />

          <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-1 px-4 py-2.5 bg-accent/40 text-[12px]">
            <span className="text-muted-foreground">Total Order <b className="text-foreground">{g.bills.length}</b></span>
            <span className="text-muted-foreground">Total Tagihan <b className="text-foreground">{formatIDR(sum(g.bills, (b) => b.amount))}</b></span>
            <span className="text-emerald-600">Dibayar {formatIDR(sum(g.bills, (b) => b.paid))}</span>
            <span className="text-rose-600">Sisa Piutang {formatIDR(sum(g.bills.filter((b) => b.payment_status !== "Lunas"), (b) => b.remaining))}</span>
          </div>
        </Card>
      ))}

      {/* Detail */}
      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Detail Tagihan {detailFor?.order_no}</DialogTitle>
            <DialogDescription className="text-xs">Rincian tagihan dan riwayat pembayaran.</DialogDescription>
          </DialogHeader>
          {detailFor && (
            <div className="space-y-3 py-1 text-sm" data-testid="bill-detail-panel">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <div><p className="text-[11px] text-muted-foreground">Pelanggan</p><p className="font-medium">{detailFor.customer_name || "-"}</p></div>
                <div><p className="text-[11px] text-muted-foreground">No. Telepon</p><p className="font-medium">{detailFor.customer_phone || "-"}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Produk</p><p className="font-medium">{detailFor.category} ×{detailFor.quantity || 1}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Admin</p><p className="font-medium">{detailFor.created_by_name || "-"}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Tgl Order</p><p className="font-medium">{formatDate(detailFor.date)}</p></div>
                <div><p className="text-[11px] text-muted-foreground">Jatuh Tempo</p><p className={`font-medium ${detailFor.overdue ? "text-rose-600" : ""}`}>{formatDate(detailFor.due_date)}</p></div>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Tagihan</span><span className="font-semibold">{formatIDR(detailFor.amount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Uang Muka</span><span>{formatIDR(detailFor.down_payment || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Dibayar</span><span className="text-emerald-600">{formatIDR(detailFor.paid)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="font-semibold">Sisa Tagihan</span><span className="font-semibold text-rose-600">{formatIDR(detailFor.remaining)}</span></div>
              </div>
              {detailFor.proof_image && (
                <div className="flex items-center justify-between rounded-lg border border-border p-2.5 text-xs" data-testid="cust-order-proof-block">
                  <span className="text-muted-foreground">Bukti pembayaran order ({detailFor.payment_status === "Lunas" ? "Lunas" : "Uang Muka"})</span>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2" data-testid="cust-order-proof" onClick={() => viewProof(detailFor.proof_image)}><Eye className="h-3 w-3" /> Lihat Bukti Pembayaran</Button>
                </div>
              )}
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">Riwayat Pembayaran</p>
                <div className="space-y-1" data-testid="bill-payment-history">
                  {(detailFor.down_payment || 0) > 0 && (
                    <div className="flex justify-between text-xs rounded-md bg-accent/40 px-2.5 py-1.5"><span>Uang Muka · {formatDate(detailFor.date)}</span><span className="font-medium">{formatIDR(detailFor.down_payment)}</span></div>
                  )}
                  {(detailFor.payments || []).map((p, i) => (
                    <div key={i} className="text-xs rounded-md bg-accent/40 px-2.5 py-1.5 space-y-0.5">
                      <div className="flex justify-between"><span>{p.account || "-"} · {formatDate(p.date)}</span><span className="font-medium">{formatIDR(p.amount)}</span></div>
                      {p.proof_image && (
                        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 px-2 mt-0.5" data-testid={`cust-proof-link-${i}`} onClick={() => viewProof(p.proof_image)}>
                          <Eye className="h-3 w-3" /> Lihat Bukti Pembayaran
                        </Button>
                      )}
                    </div>
                  ))}
                  {(detailFor.down_payment || 0) === 0 && (detailFor.payments || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">Belum ada pembayaran tercatat.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {detailFor && detailFor.payment_status !== "Lunas" && (
              <Button variant="outline" onClick={() => printInvoice(detailFor)} data-testid="bill-detail-print"><Printer className="h-4 w-4 mr-1" /> Cetak Invoice</Button>
            )}
            {detailFor && detailFor.remaining > 0 && (
              <Button variant="gold" onClick={() => { setPayProof(""); setPayFor(detailFor); setPayForm({ amount: String(detailFor.remaining), account: "Bank BCA", date: today() }); setDetailFor(null); }} data-testid="bill-detail-pay"><Wallet className="h-4 w-4 mr-1" /> Catat Pembayaran</Button>
            )}
            <Button variant="outline" onClick={() => setDetailFor(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Catat Pembayaran</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{payFor.customer_name || "Pelanggan"} — sisa <b className="text-foreground">{formatIDR(payFor.remaining)}</b></p>
              <div className="space-y-1.5"><Label>Jumlah Pembayaran (Rp)</Label><Input data-testid="bill-pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Rekening Tujuan</Label>
                <Select value={payForm.account} onValueChange={(v) => setPayForm({ ...payForm, account: v })}>
                  <SelectTrigger data-testid="bill-pay-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tanggal</Label><Input data-testid="bill-pay-date" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Bukti Gambar Pembayaran <span className="text-rose-600">(wajib)</span></Label>
                <Input data-testid="bill-pay-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadProof(e.target.files?.[0])} />
                {payProofBusy && <p className="text-[11px] text-muted-foreground">Mengunggah bukti...</p>}
                {payProof && !payProofBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="bill-pay-proof-ok">Bukti terlampir ✓</p>}
                {!payProof && !payProofBusy && <p className="text-[11px] text-rose-600">Tanpa bukti gambar, pembayaran tidak bisa disimpan.</p>}
                <p className="text-[11px] font-semibold text-amber-600" data-testid="bill-pay-proof-rule">BUKTI PEMBAYARAN HARUS SAMA DENGAN NAMA PELANGGAN</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button>
            <Button onClick={submitPay} disabled={!payProof || payProofBusy} data-testid="bill-pay-submit" variant="gold">Simpan Pembayaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Tagihan {editFor?.order_no}</DialogTitle>
            <DialogDescription className="text-xs">Perubahan berlaku pada order sumber dan ikut memperbarui laporan.</DialogDescription>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nama Pelanggan</Label><Input data-testid="bill-edit-customer" className="h-9 text-xs" value={eform.customer_name} onChange={(e) => setEform({ ...eform, customer_name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">No. Telepon</Label><Input data-testid="bill-edit-phone" className="h-9 text-xs" value={eform.customer_phone} onChange={(e) => setEform({ ...eform, customer_phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Produk / Kategori</Label>
                <Select value={eform.category} onValueChange={(v) => setEform({ ...eform, category: v })}>
                  <SelectTrigger data-testid="bill-edit-category" className="h-9 text-xs"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                  <SelectContent>{(opts?.income_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">Jumlah</Label><Input data-testid="bill-edit-qty" type="number" min="1" className="h-9 text-xs" value={eform.quantity} onChange={(e) => setEform({ ...eform, quantity: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Modal /pcs</Label><Input data-testid="bill-edit-base" type="number" className="h-9 text-xs" value={eform.unit_base} onChange={(e) => setEform({ ...eform, unit_base: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Jual /pcs</Label><Input data-testid="bill-edit-sell" type="number" className="h-9 text-xs" value={eform.unit_sell} onChange={(e) => setEform({ ...eform, unit_sell: e.target.value })} /></div>
              </div>
              <div className="rounded-lg bg-accent/50 p-2.5 text-xs flex justify-between" data-testid="bill-edit-preview">
                <span className="text-muted-foreground">Total Tagihan Baru</span>
                <span className="font-semibold">{formatIDR((parseFloat(eform.unit_sell) || 0) * (parseInt(eform.quantity) || 1))}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Cashback (Rp)</Label><Input data-testid="bill-edit-cashback" type="number" className="h-9 text-xs" value={eform.cashback} onChange={(e) => setEform({ ...eform, cashback: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Jatuh Tempo</Label><Input data-testid="bill-edit-due" type="date" className="h-9 text-xs" value={eform.customer_due_date} onChange={(e) => setEform({ ...eform, customer_due_date: e.target.value })} /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Kartu Ucapan</Label>
                <Textarea data-testid="bill-edit-description" rows={3} value={eform.description} onChange={(e) => { const v = e.target.value; const p = parseKartuUcapan(v); setEform({ ...eform, description: v, ucapan: p.ucapan, dari: p.dari, alamat: p.alamat }); }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} data-testid="bill-edit-submit" variant="gold">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
